import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Download, Trash2, Eye } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/brag-docs`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.delete(`${API}/brag-docs/${docId}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const docBodyIsBroken = (docObj) => {
    const body = (docObj && docObj.content ? String(docObj.content) : '').trim();
    return !body || body.startsWith('Error generating');
  };

  const handleExportPDF = (doc) => {
    if (docBodyIsBroken(doc)) {
      toast.error('This document did not generate. Regenerate it before exporting.');
      return;
    }
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 25;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(31, 42, 68);
      const titleLines = pdf.splitTextToSize(doc.title, maxWidth);
      titleLines.forEach(line => {
        pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 7;
      });
      yPosition += 10;

      // Date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(128, 128, 128);
      pdf.text(new Date(doc.created_at).toLocaleDateString(), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Content
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      
      // Remove emojis and clean content
      const cleanContent = doc.content.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');
      const contentLines = cleanContent.split('\n');
      
      contentLines.forEach(line => {
        // Skip label lines
        if (line.trim() === 'HEADER' || line.trim() === 'FOOTER' || 
            line.includes('Generated by BragDoc')) {
          return;
        }
        
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        
        if (line.startsWith('## ')) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(line.replace('## ', '').replace(/\d+\)\s*/g, ''), margin, yPosition);
          yPosition += 8;
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
        } else if (line.startsWith('### ')) {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(line.replace('### ', ''), margin, yPosition);
          yPosition += 7;
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
        } else if (line.startsWith('- ')) {
          const text = line.replace(/^- /, '');
          pdf.text('• ' + text, margin, yPosition);
          yPosition += 6;
        } else if (line.trim()) {
          const wrapped = pdf.splitTextToSize(line, maxWidth);
          wrapped.forEach(wLine => {
            pdf.text(wLine, margin, yPosition);
            yPosition += 6;
          });
        } else {
          yPosition += 4;
        }
      });

      pdf.save(doc.doc_id + '_brag_document.pdf');
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleExportDOCX = async (docId) => {
    const docObj = documents.find((d) => d.doc_id === docId);
    if (docObj && docBodyIsBroken(docObj)) {
      toast.error('This document did not generate. Regenerate it before exporting.');
      return;
    }
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/exports/docx/${docId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', docId + '_brag_document.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Explicit duration plus a hard dismiss: sonner pauses its timer while
      // the cursor hovers the toast, which sits over the export buttons, so
      // without the fallback the success toast can hang there indefinitely.
      const exportToastId = toast.success('Word document downloaded', { duration: 3000 });
      setTimeout(() => toast.dismiss(exportToastId), 3200);
    } catch (error) {
      console.error('Failed to export DOCX:', error);
      toast.error('Failed to export DOCX');
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              My Reports
            </h1>
            <p className="text-gray-600">{documents.length} saved brag documents</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              onClick={() => navigate('/brag-doc/new')}
              className="px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base whitespace-nowrap"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Generate New
            </Button>
          </div>
        </div>

        {/* Documents List */}
        {documents.length === 0 ? (
          <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--navy-300)' }} />
            <p className="text-gray-600 mb-4">No documents yet. Generate your first brag document!</p>
            <Button
              onClick={() => navigate('/brag-doc/new')}
              className="px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Generate Document
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {documents.map((doc) => (
              <div
                key={doc.doc_id}
                className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 hover:border-navy-200 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold mb-1 break-words" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                      {doc.title}
                    </h3>
                    <p className="text-sm text-gray-600 break-words">
                      {doc.purpose} • {new Date(doc.time_period_start).toLocaleDateString()} - {new Date(doc.time_period_end).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                      Generated on {new Date(doc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    data-testid={`view-doc-${doc.doc_id}`}
                    onClick={() => {
                      setSelectedDoc(doc);
                      setShowPreview(true);
                    }}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </Button>
                  <Button
                    data-testid={`export-pdf-${doc.doc_id}`}
                    onClick={() => handleExportPDF(doc)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </Button>
                  <Button
                    data-testid={`export-docx-${doc.doc_id}`}
                    onClick={() => handleExportDOCX(doc.doc_id)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Word
                  </Button>
                  <Button
                    data-testid={`delete-doc-${doc.doc_id}`}
                    onClick={() => handleDelete(doc.doc_id)}
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              {selectedDoc?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDoc && (
            <div>
              <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: 'JetBrains Mono' }}>
                Generated on {new Date(selectedDoc.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>

              <div className="prose prose-sm max-w-none p-6 bg-white rounded-lg border border-gray-200">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-6 mb-3" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                    p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc ml-6 mb-3 space-y-1" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                    strong: ({node, ...props}) => <strong style={{ color: 'var(--navy-900)' }} {...props} />,
                    hr: ({node, ...props}) => <hr className="my-4 border-gray-300" {...props} />,
                  }}
                >
                  {selectedDoc.content}
                </ReactMarkdown>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => handleExportPDF(selectedDoc)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button
                  onClick={() => handleExportDOCX(selectedDoc.doc_id)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export Word
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default MyDocuments;